import * as React from 'react';
import { Grid, Typography } from '@mui/material';
import { FormInput } from '~/components/FormComponents/FormInput';
import { FormYesNoQuestion } from '~/components/FormComponents/FormYesNoQuestion';
import { AddressInfo } from '~/modules/identification/components/FormComponents/AddressInfo';
import CountryInfoBlock from '~/modules/identification/components/FormComponents/CountryInfoBlock';
import { DocumentInfo } from '~/modules/identification/components/FormComponents/DocumentInfo';
import IdentificationNumberBlock from '~/modules/identification/components/FormComponents/IdentificationNumberBlock';
import PersonalInfoBlock from '~/modules/identification/components/FormComponents/PersonalInfoBlock';
import FormAutocomplete from '~/components/FormComponents/FormAutocomplete';
import { useAddressQuery } from '~/queries/useAddressQuery';
import { useGetOwnerIndex } from '../../hooks/useGetOwnerIndex';
import { Path, useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { TOwnershipStructure } from '../../types';
import { useState } from 'react';
import { DeleteModal } from '~/components/modals/DeleteModal/DeleteModal';
import FormGridItem from '~/components/FormComponents/FormGridItem';
import {
  beneficiaryFormalOwnershipTooltip,
  beneficiaryPercentageTooltip,
  beneficiaryTooltip,
} from '../../constants';
import { InfoTooltip } from '~/components/InfoTooltip/InfoTooltip';
import { commonStyles } from '~/theme/styles/commonStyles';

interface IIndividualOwnerFormProps {
  index: number;
  isFirstLevel?: boolean;
  isIndividualParent?: boolean;
  isWithoutFormalOwnership?: boolean;
  handlePercentageChange: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
}

export const IndividualOwnerForm = React.memo(
  ({
    index,
    isFirstLevel,
    isIndividualParent,
    handlePercentageChange,
    isWithoutFormalOwnership,
  }: IIndividualOwnerFormProps) => {
    const { countryOptions } = useAddressQuery();
    const { ownerIndex } = useGetOwnerIndex(index);
    const { setValue, control, getValues, trigger } = useFormContext<TOwnershipStructure>();
    const { remove: removeOwner } = useFieldArray({ control, name: 'owners' });
    const { remove: removeLink } = useFieldArray({ control, name: 'link' });
    const [showModal, setShowModal] = useState(false);
    const [isBeneficiary, links, owners] = useWatch({
      control,
      name: [`owners.${ownerIndex}.isBeneficiary`, 'link', 'owners'],
    });
    const tooltipTitle = isWithoutFormalOwnership
      ? beneficiaryFormalOwnershipTooltip
      : isBeneficiary || isIndividualParent
        ? ''
        : beneficiaryTooltip;

    const percentageInputLabel = isWithoutFormalOwnership ? (
      <Typography variant="body2" sx={{ ...commonStyles.centerFlexContainer, gap: 1 }}>
        Частка в статутному капіталі (%)
        <InfoTooltip title={beneficiaryPercentageTooltip} placement="right" />
      </Typography>
    ) : (
      'Частка в статутному капіталі (%)'
    );

    const ownerId = links[index]?.fromNodeId;

    const handleIsBeneficiaryChange = (isChecked: boolean) => {
      if (isChecked) {
        if (links.some((item) => item.toNodeId === ownerId)) {
          setShowModal(true);
          return;
        }
      }
      setValue(`owners.${ownerIndex}.isBeneficiary`, isChecked, {
        shouldValidate: true,
        shouldDirty: true,
      });
    };

    const handleDeleteChildren = () => {
      const linkToDelete = links.find((item) => item.toNodeId === ownerId);
      const childOwner = owners.find((item) => item.id === linkToDelete?.fromNodeId);
      const childOwnerLinks = links.filter((item) => childOwner?.id === item.fromNodeId);
      const childOwnerIndex = owners.findIndex((item) => item.id === linkToDelete?.fromNodeId);

      removeLink(links.findIndex((item) => item.toNodeId === ownerId));

      if (childOwnerLinks.length <= 1) {
        removeOwner(childOwnerIndex);
      }
      setValue(`owners.${ownerIndex}.isBeneficiary`, true, {
        shouldValidate: true,
        shouldDirty: true,
      });
      setShowModal(false);
    };

    const handleBlur = (fieldName: string) => {
      const owners = getValues('owners');
      owners.forEach((_, index) => {
        trigger(`owners.${index}.${fieldName}` as Path<TOwnershipStructure>);
      });
    };

    return (
      <>
        <Grid container spacing={3}>
          <Grid size={12}>
            <FormYesNoQuestion
              name={`owners.${ownerIndex}.isBeneficiary`}
              question="Чи є особа кінцевим бенефіціарним власником?"
              tooltipTitle={tooltipTitle}
              onChange={handleIsBeneficiaryChange}
              disableFieldOnChange
              disabled={isWithoutFormalOwnership}
            />
          </Grid>
          <PersonalInfoBlock section={`owners.${ownerIndex}`} />
          <IdentificationNumberBlock section={`owners.${ownerIndex}`} onBlur={handleBlur} />
          <CountryInfoBlock
            section={`owners.${ownerIndex}`}
            isBirthPlace={isFirstLevel || !!isBeneficiary}
            isBirthplaceCountry={isFirstLevel || !!isBeneficiary}
          />
          {(isFirstLevel || isBeneficiary) && (
            <DocumentInfo
              citizenshipCountryPath={`owners.${ownerIndex}.citizenshipCountry`}
              residenceCountryPath={`owners.${ownerIndex}.residenceCountry`}
              section={`owners.${ownerIndex}`}
            />
          )}
          {isFirstLevel || isBeneficiary ? (
            <AddressInfo section={`owners.${ownerIndex}`} isPersonAddress />
          ) : (
            <>
              <FormGridItem>
                <FormAutocomplete
                  name={`owners.${ownerIndex}.address.registrationAddress.country`}
                  options={countryOptions}
                  inputProps={{
                    placeholder: 'Оберіть країну місця реєстрації',
                  }}
                  label="Країна місця реєстрації"
                  fullWidth
                />
              </FormGridItem>
              <FormGridItem>
                <FormAutocomplete
                  name={`owners.${ownerIndex}.address.permanentAddress.country`}
                  options={countryOptions}
                  inputProps={{
                    placeholder: 'Оберіть країну місця проживання',
                  }}
                  label="Країна місця проживання"
                  fullWidth
                />
              </FormGridItem>
            </>
          )}
          <FormGridItem sx={{ alignContent: 'flex-end' }}>
            <FormInput
              name={`link.${index}.ownershipPercentage`}
              placeholder="Введіть частку в статутному капіталі"
              label={percentageInputLabel}
              fullWidth
              onChange={handlePercentageChange}
              disableFormOnChange
              disabled={isWithoutFormalOwnership}
            />
          </FormGridItem>
        </Grid>
        <DeleteModal
          closeDeleteModal={() => setShowModal(false)}
          confirmDelete={handleDeleteChildren}
          showDeleteModal={showModal}
          title="Якщо ви бажаєте внести зміни, пов'язана ФО буде видалена зі структури. Продовжити?"
          hideIcon
          cancelBtnText="Скасувати"
          confirmBtnText="Продовжити"
        />
      </>
    );
  }
);
